import { ApiError, getJson } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';

// FD-435 (FD-ADR-041 decision 8) — the console half of `GET /v1/license-balance`,
// added by FD-434.
//
// ── WHY THIS MODULE EXISTS AT ALL ──────────────────────────────────────────
//
// FD-ADR-041 changed the licensing UNIT from the campaign to the device:
// activating a 500-device wave now reserves 500 units, not one. An operator
// about to spend that needs to see what they are drawing on BEFORE they draw on
// it, and nothing else in the platform can answer the question — the counts on a
// migration row say what ONE WAVE reserved, never what the account has.
//
// ── THE ONE RULE THIS MODULE'S CALLERS MUST HOLD ───────────────────────────
//
//   CALL THIS ON DIALOG OPEN. NEVER ON A POLL.
//
// FD-ADR-032 §4's reasoning, applied to a read. ATC Licensing runs HA-off
// (ATC-ADR-007) with 5–30 s cold starts, and the activation dialog polls
// `activate-wave` every ~5 s for up to 15 minutes. A balance read wired into
// that loop is ~180 outbound calls per activation against a service that is
// cold — which is how a read becomes an outage for the write beside it.
//
// This module cannot enforce that, and neither can the endpoint (its own header
// comment says so). `useLicenseBalance` in `LicenseBalancePanel.tsx` is where
// the policy actually lives, and `ActivateWaveDialog.test.tsx` asserts it by
// advancing timers across several poll intervals and requiring the call count to
// still be 1 — a test rather than a comment, because a comment does not fail.
//
// ── AND THE ONE VALUE THAT MUST NOT BE "TIDIED" ────────────────────────────
//
// `consumable.available` MAY BE NEGATIVE, and it is passed through unclamped all
// the way from ATC. The 1:1 bucket is advisory, so an account can legitimately
// be over-drawn — clamping to zero, or painting it as an error, would hide
// exactly the fact an operator opened the dialog to see.

/**
 * The 1:1 consumable bucket. ADVISORY by default (FD-ADR-024 D8, re-affirmed by
 * FD-ADR-041 decision 7) — a shortfall is recorded and activation proceeds
 * unless a deployment sets `LICENSING_ENFORCE_CONSUMABLE=true`.
 *
 * Every member is `number | null`. The producer contract states all four, but a
 * console that renders a missing field as `0` reports a balance it was never
 * told — the precise defect this whole chain of tasks exists to prevent.
 */
export interface ConsumableBalance {
  granted: number | null;
  burned: number | null;
  reservedOpen: number | null;
  /** May be NEGATIVE. Never clamp; never colour as an error. */
  available: number | null;
}

/**
 * The concurrency pool. This is the bucket that HARD-BLOCKS on every
 * deployment: `409 LICENSE_POOL_EXHAUSTED` refuses a wave activation outright,
 * and since FD-ADR-041 it fires on `poolAvailable < N` — one slot per assigned
 * device — rather than `< 1`.
 *
 * It is the ALWAYS-ON block, not the only one. `409 LICENSE_CREDITS_EXHAUSTED`
 * also refuses, but only where a deployment has set
 * `LICENSING_ENFORCE_CONSUMABLE=true`, which ships `false` (FD-ADR-041
 * decision 7). The console cannot read that setting, so no copy here may
 * promise that a credit shortfall proceeds.
 */
export interface PoolBalance {
  capacity: number | null;
  inUse: number | null;
  available: number | null;
}

/**
 * `GET /v1/license-balance` — 200 `data`.
 *
 * Either bucket may be `null`: a deployment on a pool-only or consumable-only
 * account gets `null` for the one it does not have, and FD-434 emits both keys
 * either way so the console has ONE shape to render.
 */
export interface LicenseBalance {
  consumable: ConsumableBalance | null;
  pool: PoolBalance | null;
}

export async function getLicenseBalance(): Promise<LicenseBalance> {
  const envelope = await getJson<ApiEnvelope<LicenseBalance>>('/v1/license-balance');
  return envelope.data;
}

/**
 * The machine code off a failed balance read — **returned verbatim, not mapped
 * onto a closed union**, which is a deliberate departure from `waveErrorCode` /
 * `ppkgErrorCode`.
 *
 * Those two enumerate codes FLIGHT DECK defines, so a closed set is a real
 * guarantee. This endpoint passes **ATC's OWN `error.code`** through
 * (`account_not_mapped`, `licensing_unavailable`, an auth rejection…) precisely
 * so three different operator actions stay three different answers — FD-346 is
 * the precedent, where an errorCode discarded in a retry path left an
 * actionable `licensing_unavailable` recorded as an empty string. Enumerating
 * someone else's open vocabulary here would re-introduce that flattening one
 * producer release later, and it would do it silently.
 *
 * Exactly one code is branched on by the UI, and it is AirBridge's own:
 * `CONFIG_MISSING` (503) means licensing is switched off in this deployment —
 * not broken, and not something an operator can act on by retrying.
 */
export function licenseBalanceErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const code = isApiEnvelope(err.body) ? err.body.error?.code : undefined;
  const text = typeof code === 'string' ? code.trim() : '';
  return text.length > 0 ? text : null;
}

/** `LICENSING_URL` is unset — licensing is off here, which is a fact, not a fault. */
export function isLicensingNotConfigured(err: unknown): boolean {
  return licenseBalanceErrorCode(err) === 'CONFIG_MISSING';
}
